import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges, AfterViewInit, OnDestroy } from '@angular/core';


@Component({
  selector: 'app-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss']
})
export class VideoPlayerComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() url: string;
  @Input() preventForwardSeek: boolean = false;
  @Input() storageKey: string;
  @Output() videoCompleted = new EventEmitter<void>();
  @Output() watchedProgress = new EventEmitter<number>();

  @ViewChild('videoEl', { static: false }) videoEl: ElementRef<HTMLVideoElement>;

  private maxReachedTime = 0;
  private lastTickTime = 0;
  private completedEmitted = false;
  private saveThrottleTimer: any = null;

  get videoUrl() {
    if (this.url && this.url.length) {
      return this.url;
    }
    return '';
  }

  constructor(){}
  ngOnInit() {

  }

  ngAfterViewInit() {
    this.restoreProgress();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.url && !changes.url.firstChange) {
      this.maxReachedTime = 0;
      this.lastTickTime = 0;
      this.completedEmitted = false;
    }
  }

  ngOnDestroy() {
    // Flush latest position so an accidental close doesn't lose the last few seconds.
    this.flushSave();
  }

  onTimeUpdate() {
    const video = this.videoEl && this.videoEl.nativeElement;
    if (!video) { return; }

    // timeupdate fires during seeks too — only credit natural playback,
    // and cap per-tick advance so a stealth seek can't inflate the ceiling.
    if (!video.seeking) {
      const delta = video.currentTime - this.lastTickTime;
      if (delta > 0 && delta < 1) {
        this.maxReachedTime = Math.max(this.maxReachedTime, video.currentTime);
      }
    }
    this.lastTickTime = video.currentTime;

    if (video.duration > 0 && isFinite(video.duration)) {
      const pct = Math.min(100, Math.round((this.maxReachedTime / video.duration) * 100));
      this.watchedProgress.emit(pct);

      // Fallback: some browsers/streams don't fire 'ended' cleanly at 100%.
      if (!this.completedEmitted && video.currentTime >= video.duration - 0.5) {
        this.markCompleted();
      }
    }

    this.scheduleSave();
  }

  onSeeking() {
    if (!this.preventForwardSeek) { return; }
    const video = this.videoEl && this.videoEl.nativeElement;
    if (!video) { return; }
    if (video.currentTime > this.maxReachedTime + 0.5) {
      video.currentTime = this.maxReachedTime;
    }
  }

  onEnded() {
    if (this.completedEmitted) { return; }
    this.markCompleted();
  }

  onSeeked() {
    // Flush immediately so a rewind/restart is persisted before the user closes the modal.
    this.flushSave();
  }

  onPause() {
    this.flushSave();
  }

  rewind(seconds: number = 10) {
    const video = this.videoEl && this.videoEl.nativeElement;
    if (!video) { return; }
    video.currentTime = Math.max(0, video.currentTime - seconds);
  }

  restart() {
    const video = this.videoEl && this.videoEl.nativeElement;
    if (!video) { return; }
    video.currentTime = 0;
  }

  private markCompleted() {
    this.completedEmitted = true;
    this.clearProgress();
    this.videoCompleted.emit();
  }

  private restoreProgress() {
    if (!this.storageKey) { return; }
    let parsed: any;
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) { return; }
      parsed = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!parsed || typeof parsed.maxReachedTime !== 'number' || parsed.maxReachedTime <= 0) { return; }

    const video = this.videoEl && this.videoEl.nativeElement;
    if (!video) { return; }

    this.maxReachedTime = parsed.maxReachedTime;
    // Resume where they left off — may be earlier than maxReachedTime after a rewind.
    const resumeAt = typeof parsed.currentPosition === 'number' && parsed.currentPosition > 0
      ? parsed.currentPosition
      : parsed.maxReachedTime;

    const seek = () => {
      // Clamp in case the video's duration is now shorter than the stored position.
      const target = isFinite(video.duration) && video.duration > 0
        ? Math.min(resumeAt, video.duration - 0.1)
        : resumeAt;
      video.currentTime = target;
      this.lastTickTime = target;
    };

    if (video.readyState >= 1) {
      seek();
    } else {
      video.addEventListener('loadedmetadata', seek, { once: true });
    }
  }

  private scheduleSave() {
    if (!this.storageKey || this.saveThrottleTimer) { return; }
    this.saveThrottleTimer = setTimeout(() => {
      this.saveProgress();
      this.saveThrottleTimer = null;
    }, 2000);
  }

  private flushSave() {
    if (this.saveThrottleTimer) {
      clearTimeout(this.saveThrottleTimer);
      this.saveThrottleTimer = null;
    }
    this.saveProgress();
  }

  private saveProgress() {
    if (!this.storageKey || this.maxReachedTime <= 0 || this.completedEmitted) { return; }
    const video = this.videoEl && this.videoEl.nativeElement;
    const currentPosition = video ? video.currentTime : this.maxReachedTime;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({
        maxReachedTime: this.maxReachedTime,
        currentPosition,
        updatedAt: Date.now()
      }));
    } catch (e) {
      // storage unavailable / quota exceeded — silently skip
    }
  }

  private clearProgress() {
    if (!this.storageKey) { return; }
    try {
      localStorage.removeItem(this.storageKey);
    } catch (e) {}
  }

}
